import { createApi } from '@reduxjs/toolkit/query/react';

import { SCOPE_ASSESSOR_API_URL } from 'api/APIBase';
import { mapEnrollmentChangeHistory } from 'services/enrollmentHistoryHelpers';
import { handleError, handleRTKQueryError } from 'services/Error';
import { mapMemberSummary } from 'services/treatmentHelpers';
import { getBaseQuery } from 'store/api/apiBaseQuery';

const TagTypes = {
    MEMBER: 'PATCH_MEMBER',
};

export const scopeAssessorApi = createApi({
    baseQuery: getBaseQuery(SCOPE_ASSESSOR_API_URL),
    tagTypes: Object.values(TagTypes),
    reducerPath: 'scopeAssessorApi',
    endpoints: (build) => ({
        getMemberDetails: build.query({
            query: ({ payerId, segmentId, memberId, firstName, lastName, dateOfBirth }) => ({
                url: `admin/${payerId}/members/${memberId}`,
                params: {
                    segment_id: segmentId,
                    firstname: firstName,
                    lastname: lastName,
                    dateofbirth: dateOfBirth,
                    ignore_dates: true,
                },
            }),
            transformResponse: mapMemberSummary,
            providesTags: (_results, _error, { memberId }) => [
                { type: TagTypes.MEMBER, id: memberId },
            ],
            transformErrorResponse: (err) => {
                if (err.status === 404) {
                    handleError('Member not found.', err);
                } else {
                    handleError('An error occurred retrieving member details.', err);
                }
            },
        }),
        patchMemberPhone: build.mutation({
            query: ({
                payerId,
                memberId,
                firstName,
                dateOfBirth,
                memberPhone,
                treatmentId,
                reason,
            }) => ({
                url: `admin/${payerId}/members/phone/${memberId}`,
                method: 'PATCH',
                params: {
                    firstname: firstName,
                    dateofbirth: dateOfBirth,
                    treatment_id: treatmentId,
                },
                body: {
                    phone: memberPhone,
                    reason,
                },
            }),
            invalidatesTags: (_results, _error, { memberId }) => [
                { type: TagTypes.MEMBER, id: memberId },
            ],
        }),
        getBenefitCheckConfig: build.query({
            query: ({ payerId, segmentId }) => ({
                url: `${payerId}/benefit_check_config/${segmentId}`,
                params: {
                    option_name: 'delayed_unmanagement_days',
                },
            }),
        }),
        patchMemberAddress: build.mutation({
            query: ({
                payerId,
                memberId,
                firstName,
                dateOfBirth,
                memberAddress1,
                memberAddress2,
                memberCity,
                memberState,
                memberZip,
                referralId,
                treatmentId,
                reason,
            }) => ({
                url: `admin/${payerId}/members/address/${memberId}`,
                method: 'PATCH',
                params: {
                    firstname: firstName,
                    dateofbirth: dateOfBirth,
                    treatment_id: treatmentId,
                },
                body: {
                    address1: memberAddress1,
                    address2: memberAddress2,
                    city: memberCity,
                    state: memberState,
                    zip: memberZip,
                    referral_id: referralId,
                    reason,
                },
            }),
            invalidatesTags: (_results, _error, { memberId }) => [
                { type: TagTypes.MEMBER, id: memberId },
            ],
        }),
        getEnrollmentChangeHistory: build.query({
            query: ({ payerId, memberId, firstName, lastName, dateOfBirth }) => ({
                url: `${payerId}/enrollment_history/${memberId}`,
                params: {
                    firstname: firstName,
                    lastname: lastName,
                    dateofbirth: dateOfBirth,
                },
            }),
            transformResponse: (response) => response?.map(mapEnrollmentChangeHistory),
            transformErrorResponse: (err) => {
                if (err.status === 404) {
                    handleError('Enrollment change history not found.', err);
                } else {
                    handleError('An error occurred retrieving enrollment change history.', err);
                }
            },
        }),
        getMemberAttributes: build.query({
            query: ({ payerId, memberId, firstName, dateOfBirth }) => ({
                url: `admin/${payerId}/members/attributes/lookup`,
                method: 'POST',
                body: {
                    member_id: memberId,
                    firstname: firstName,
                    dateofbirth: dateOfBirth,
                },
            }),
            transformResponse: (response) => response ?? [],
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving member attributes.',
            ),
        }),
    }),
});

export const {
    useGetMemberDetailsQuery,
    useGetBenefitCheckConfigQuery,
    useGetEnrollmentChangeHistoryQuery,
    useGetMemberAttributesQuery,
    usePatchMemberPhoneMutation,
    usePatchMemberAddressMutation,
} = scopeAssessorApi;
